/**
 * 排程工作頁面模組
 */

//-----------------------------------------------------------------------
// 載入模組
import * as React from 'react';
import { JSX } from 'react';
import get from 'lodash/get';
import { useRecordContext, useShowController, useEditContext, usePermissions, ReferenceArrayField, SingleFieldList, ChipField, NumberField, DateTimeInput, maxLength, DataProvider, downloadCSV, ExportButton, Form, Toolbar, EditContextProvider } from 'react-admin';
import { Create, Edit, List, Show, SimpleForm, TabbedShowLayout, Tab, Datagrid, Pagination, Labeled, ReferenceManyField } from 'react-admin';
import { TextInput, SelectInput, TextField, DateField, ShowButton, ReferenceField } from 'react-admin';
import { SaveOnlyToolbar, ListEditButton, MemberLogTypeField, MemberLogDataField, TextUrlField, ScheduleKeywordFilter } from '../lib/components';
import jsonExport from 'jsonexport/dist';

//-----------------------------------------------------------------------
// defines
import { SCHEDULE_STATUS, MEMBER_GROUP, MEMBER_LOG_TYPE, SCHEDULE_TASK } from '../common/defines';
import { MemberAuthInfo } from '../providers/authProvider';
import { ChannelStatusField } from './channel';
import { Grid } from '@mui/material';
import { NamePairInfo } from '../common/interfaces';

type ScheduleTask =
{
    id		: number
    task	: SCHEDULE_TASK
    status	: SCHEDULE_STATUS
};

//-----------------------------------------------------------------------
/** 排程工作狀態顯示欄位元件。 */

const scheduleStatusMap = new Map<string, string>();
const scheduleStatusList : NamePairInfo<string>[] =
[
	{ id: SCHEDULE_STATUS.Waiting,		name: "等待中" },
	{ id: SCHEDULE_STATUS.Processing,	name: "執行中" },
	{ id: SCHEDULE_STATUS.Finished,		name: "已完成" },
	{ id: SCHEDULE_STATUS.Failed,		name: "執行失敗" },
	{ id: SCHEDULE_STATUS.Deleted,		name: "已刪除" }
];
const scheduleEditStatusList : NamePairInfo<string>[] =
[
	{ id: SCHEDULE_STATUS.Waiting,		name: "等待中" },
	{ id: SCHEDULE_STATUS.Deleted,		name: "已刪除" }
];

for(let status of scheduleStatusList)
	scheduleStatusMap.set(status.id, status.name);

export const ScheduleStatusField = (props: any) =>
{
	const { source } = props;
	const record = useRecordContext(props);

	return (<span className="MuiTypography-body2 MuiTypography-root">{scheduleStatusMap.get(get(record, source))}</span>);
}

ScheduleStatusField.defaultProps =
{
	label: '狀態',
	addLabel: true,
};

//-----------------------------------------------------------------------
/** 排程工作種類顯示欄位元件。 */

const scheduleTaskMap = new Map<string, string>();
const scheduleTaskList : NamePairInfo<string>[] =
[
	{ id: SCHEDULE_TASK.ManuallyTrack,	name: "追蹤資料" },
];

for(let task of scheduleTaskList)
	scheduleTaskMap.set(task.id, task.name);

export const ScheduleTaskField = (props: any) =>
{
	const { source } = props;
	const record = useRecordContext(props);

	return (<span className="MuiTypography-body2 MuiTypography-root">{scheduleTaskMap.get(get(record, source))}</span>);
}

ScheduleTaskField.defaultProps =
{
	label: '工作種類',
	addLabel: true,
};

//-----------------------------------------------------------------------
/** 資料輸出功能。 */
const trackLogExporter = (records: any, fetchRelatedRecords: (data: any, field: string, resource: string) => Promise<any>, dataProvider: DataProvider) =>
{
	fetchRelatedRecords(records, 'sourceId', 'source').then((sources) =>
	{
		fetchRelatedRecords(records, 'regionId', 'region').then((regions) =>
		{
			fetchRelatedRecords(records, 'kolId', 'kol').then((kols) =>
			{
				fetchRelatedRecords(records, 'keywordIds', 'keyword').then((keywords) =>
				{
					const data = records.map((record: any) =>
					{
						let	keywordIds = record.keywordIds as number[];
						let keywordList : string[] = [];

						for(let keywordId of keywordIds)
						{
							if(keywords[keywordId])
								keywordList.push(keywords[keywordId].text);
						}

						return {
							...record,
							sourceId: sources[record.sourceId].name,
							regionId: regions[record.regionId].name,
							kolId: kols[record.kolId] ? kols[record.kolId].name : '',
							keywordIds: keywordList
						};
					});

					return jsonExport(data,
					{
						headers	: [ 'id', 'sourceId', 'regionId', 'kolId', 'name', 'url', 'keywordIds', 'targetCount', 'viewCount', 'commentCount', 'status', 'createTime' ],
						rename	: [ 'id', '資料來源', '區域', '所屬 KOL', '頻道名稱', '頻道網址', '關鍵字', '目標數量', '累計瀏覽量', '留言數量', '狀態', '建立時間' ]
					}, (err, csv) =>
					{
						downloadCSV("\ufeff" + csv, 'scheduleTargetLog');
					});
				});
			});
		});
	});
};

//-----------------------------------------------------------------------
/** 排程工作列表頁面。 */
export const ScheduleList = (props: any) =>
{
	let { permissions } = usePermissions();
	let	authInfo = permissions as MemberAuthInfo;

	return <List {...props} title='排程工作' >
		<Datagrid bulkActionButtons={false}>
			<TextField source='id' />
			<ReferenceField label="操作者" source="memberId" reference="member">
				<TextField source="name" />
			</ReferenceField>
			<ScheduleTaskField source='task' />
			<TextField source='parameters' label='工作參數' />
			<DateField source='time' label='排定時間' showTime />
			<ScheduleStatusField source='status' />
			<ListEditButton edit={authInfo.group <= MEMBER_GROUP.Manager} />
			<ShowButton />
		</Datagrid>
	</List>;
}

/** 排程工作資訊頁面。 */
export const ScheduleShow = (props: any) =>
{
	let [ keywordId, setKeywordId ] = React.useState<number>(0);
	const { record, isLoading } = useShowController();
	let { permissions } = usePermissions();
	let	authInfo = permissions as MemberAuthInfo;
	let opLogs : JSX.Element = <></>;
	let taskLogs : JSX.Element = <></>;

	if(isLoading)
		return <></>;

	if(authInfo.group <= MEMBER_GROUP.Manager && authInfo.scheduleCreate)
	{
		// 操作記錄列表。
		opLogs = <Tab label='操作記錄'>
			<ReferenceManyField reference='memberLog' target='ml_int1' filter={{ ml_int1: props.id, 'type>=': MEMBER_LOG_TYPE.ScheduleCreate, 'type<': MEMBER_LOG_TYPE.KolCreate }} pagination={<Pagination />}>
				<Datagrid bulkActionButtons={false}>
					<TextField source='id' />
					<ReferenceField label="操作者" source="memberId" reference="member">
						<TextField source="name" />
					</ReferenceField>
					<MemberLogTypeField label='執行動作' />
					<MemberLogDataField label='動作內容' />
					<DateField source='logTime' label='紀錄時間' showTime />
					<TextField source='remoteAddress' label='遠端位址' />
				</Datagrid>
			</ReferenceManyField>
		</Tab>;
	}

	if(record.status === SCHEDULE_STATUS.Finished && record.task === SCHEDULE_TASK.ManuallyTrack)
	{
		// 追蹤頻道列表。
		taskLogs = <Tab label='追蹤頻道'>
			<ReferenceManyField reference='scheduleTargetLog' target='stl_st_id' filter={{ st_id: record.id, keywordId: keywordId }} pagination={<Pagination />} sort={{ field:'id',order:'ASC' }} >
				<Toolbar>
					<Grid container spacing={2}  >
						<Grid size={11}><Form><ScheduleKeywordFilter onChanged={(ids) => {setKeywordId(ids)}} /></Form></Grid>
						<Grid size={1}><ExportButton exporter={trackLogExporter} /></Grid>
					</Grid>
				</Toolbar>
				<Datagrid bulkActionButtons={false}>
					<TextField source='id' />
					<ReferenceField label="資料來源" source="sourceId" reference="source">
					<TextField source="name" />
					</ReferenceField>
					<ReferenceField label="區域" source="regionId" reference="region">
						<TextField source="name" />
					</ReferenceField>
					<ReferenceField label="所屬 KOL" source="kolId" reference="kol">
						<TextField source="name" />
					</ReferenceField>
					<TextUrlField source='url' textSource='name' label='頻道連結' target='_blank' sortable={false} />
					<ReferenceArrayField label="關鍵字" source="keywordIds" reference="keyword" sortable={false} >
						<SingleFieldList>
							<ChipField source="text" />
						</SingleFieldList>
					</ReferenceArrayField>
					<NumberField source='targetCount' label='目標數' />
					<NumberField source='targetIncrease' label='新增目標數' />
					<NumberField source='viewCount' label='瀏覽量' />
					<NumberField source='viewOfNew' label='新增目標瀏覽量' />
					<NumberField source='reactCount' label='累計反應量' />
					<NumberField source='reactOfNew' label='新增目標反應量' />
					<NumberField source='commentCount' label='留言量' />
					<NumberField source='commentOfNew' label='新增目標留言量' />
					<ChannelStatusField source='status' />
					<DateField source='createTime' label='資料加入時間' showTime />
				</Datagrid>
			</ReferenceManyField>
		</Tab>;
	}

	return <Show {...props}>
		<TabbedShowLayout>
			<Tab label='總覽'>
				<TextField source='id' />
				<ReferenceField label="操作者" source="memberId" reference="member" sortBy='ml_m_id'>
					<TextField source="name" />
				</ReferenceField>
				<ScheduleTaskField source='task' />
				<TextField source='parameter' label='工作參數' />
				<DateField source='time' label='排定時間' showTime />
				<ScheduleStatusField source='status' />
			</Tab>
			{taskLogs}
			{opLogs}
		</TabbedShowLayout>
	</Show>;
}

const EditInputs = (props: any) =>
{
	const { record } = useEditContext<ScheduleTask>();
	let inputs : JSX.Element = <></>;

	if(record?.status !== SCHEDULE_STATUS.Finished)
	{
		inputs = <>
			<Labeled label="工作種類"><SelectInput source='task' choices={scheduleTaskList} label="工作種類" /></Labeled>
			<Labeled label="工作參數"><TextInput source='parameter' label='工作參數' defaultValue={''} /></Labeled>
			<Labeled label="排定時間"><DateTimeInput source='time' label='排定時間' /></Labeled>
			<Labeled label="狀態"><SelectInput source='status' choices={scheduleEditStatusList} label="狀態" /></Labeled>
		</>;
	}
	else
	{
		inputs = <>
			<Labeled label="工作種類"><ScheduleTaskField source='task' /></Labeled>
			<Labeled label="工作參數"><TextField source='parameter' label='工作參數' defaultValue={''} /></Labeled>
			<Labeled label="排定時間"><DateField source='time' label='排定時間' showTime /></Labeled>
			<Labeled label="狀態"><ScheduleStatusField source='status' /></Labeled>
		</>;
	}

	return inputs;
}

/** 排程工作編輯頁面。 */
export const ScheduleEdit = (props: any) => {
	return <Edit {...props}>
		<SimpleForm toolbar={<SaveOnlyToolbar/>}>
			<Labeled label="ID"><TextField source='id' /></Labeled>
			<Labeled label="操作者">
				<ReferenceField source="memberId" reference="member" sortBy='ml_m_id'>
					<TextField source="name" />
				</ReferenceField>
			</Labeled>
			<EditInputs />
		</SimpleForm>
	</Edit>
};

/** 排程工作建立頁面。 */
export const ScheduleCreate = (props: any) => (
	<Create {...props}>
		<SimpleForm>
			<SelectInput source='task' choices={scheduleTaskList} label="工作種類" />
			<TextInput source='parameter' label='工作參數' validate={[maxLength(200)]} defaultValue={''} />
			<DateTimeInput source='time' label='排定時間' />
		</SimpleForm>
	</Create>
);
